import type { BoatPhotoContentType } from "../../../common/src";

const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.85;

export interface PreparedImage {
  contentType: BoatPhotoContentType;
  dataBase64: string;
}

const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error("Couldn't process this image.")),
      "image/jpeg",
      JPEG_QUALITY
    );
  });

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(new Error("Couldn't read this image."));
    reader.readAsDataURL(blob);
  });

/**
 * Re-encodes a user-chosen photo as a JPEG no larger than 1600px on its longest side.
 * This applies the camera's rotation, keeps uploads small, and drops embedded
 * metadata such as GPS location.
 */
export const prepareImage = async (file: File): Promise<PreparedImage> => {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new Error(
      "Couldn't read this image. Please use a JPEG, PNG, or WebP photo."
    );
  }
  try {
    const scale = Math.min(
      1,
      MAX_DIMENSION / Math.max(bitmap.width, bitmap.height)
    );
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Couldn't process this image.");
    }
    // JPEG has no transparency; paint white so transparent PNGs don't turn black.
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return {
      contentType: "image/jpeg",
      dataBase64: await blobToBase64(await canvasToBlob(canvas)),
    };
  } finally {
    bitmap.close();
  }
};
